import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useState,
} from "react";

import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

import Search from "@mui/icons-material/Search";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

import { getTagFromKey } from "dwv";
import type { Metadata } from "./DwvViewer.tsx";

type DicomTag = {
  name: string;
  value: string;
};

export default function TagsTable({
  fullMetaData,
}: {
  fullMetaData: Metadata | undefined;
}) {
  const [currentFullData, setCurrentFullData] = useState<DicomTag[]>();
  const [displayData, setDisplayData] = useState<DicomTag[] | undefined>([]);
  const [searchFor, setSearchFor] = useState("");
  const [instanceNumbers, setInstanceNumbers] = useState<number[]>([]);
  const [instanceNumber, setInstanceNumber] = useState(0);
  const [sliderMax, setSliderMax] = useState(1);

  useEffect(() => {
    // set slider with instance numbers ('00200013')
    const instanceElement = fullMetaData && fullMetaData["00200013"];
    if (typeof instanceElement !== "undefined") {
      const instanceNumberValue = instanceElement.value;
      // convert string to numbers
      const instNums = instanceNumberValue
        .map(Number)
        .sort((a: number, b: number) => a - b);
      setInstanceNumbers(instNums);
      setSliderMax(instNums.length);
      setInstanceNumber(instNums[0]);
    } else {
      setInstanceNumber(0);
    }
  }, [fullMetaData]);

  useEffect(() => {
    const updatedList = currentFullData?.filter(function (item: DicomTag) {
      return (
        item.name.toLowerCase().indexOf(searchFor) !== -1 ||
        item.value.toLowerCase().indexOf(searchFor) !== -1
      );
    });
    setDisplayData(updatedList);
  }, [searchFor, currentFullData]);

  const getDicomTagReducer = useCallback(
    (tagData: Metadata, prefix: string) => {
      return (accumulator: DicomTag[], currentValue: string) => {
        const tag = getTagFromKey(currentValue);
        let key = tag.getNameFromDictionary();
        if (typeof key === "undefined") {
          // add 'x' to help sorting
          key = "x" + tag.getKey();
        }
        const name = key;
        const element = tagData[currentValue];
        let value = element.value;
        // possible 'merged' object
        // (use slice method as test for array and typed array)
        if (
          typeof value.slice === "undefined" &&
          typeof value[instanceNumber] !== "undefined"
        ) {
          value = value[instanceNumber];
        }
        // force instance number (otherwise takes value in non indexed array)
        if (name === "InstanceNumber") {
          value = [instanceNumber];
        }
        // recurse for sequence
        if (element.vr === "SQ") {
          // sequence tag
          accumulator.push({
            name: (prefix ? prefix + " " : "") + name,
            value: "",
          });
          // sequence value
          for (let i = 0; i < value.length; ++i) {
            const sqItems = value[i];
            const keys = Object.keys(sqItems);
            const res = keys.reduce(
              getDicomTagReducer(sqItems, prefix + "[" + i + "]"),
              [],
            );
            accumulator = accumulator.concat(res);
          }
        } else {
          // shorten long other data
          if (element.vr[0] === "O" && value.length > 10) {
            value = [
              value.slice(0, 10).toString() + "... (len:" + value.length + ")",
            ];
          }
          accumulator.push({
            name: (prefix ? prefix + " " : "") + name,
            value: value.toString(),
          });
        }
        return accumulator;
      };
    },
    [instanceNumber],
  );

  const getMetaArray = useCallback(() => {
    if (!fullMetaData) return;
    let reducer;
    if (typeof fullMetaData["00020010"] !== "undefined") {
      reducer = getDicomTagReducer(fullMetaData, "");
    } else {
      reducer = getTagReducer(fullMetaData);
    }
    const keys = Object.keys(fullMetaData);
    return keys.reduce(reducer, []);
  }, [fullMetaData, getDicomTagReducer]);

  function getTagReducer(tagData: Metadata) {
    return function (accumulator: DicomTag[], currentValue: string) {
      accumulator.push({
        name: currentValue,
        value: tagData[currentValue].value.join(", "),
      });
      return accumulator;
    };
  }

  function onSliderChange(_event: Event, value: number) {
    const instanceNum = instanceNumbers[value - 1];
    const metaArray = getMetaArray();
    setInstanceNumber(instanceNum);
    setCurrentFullData(metaArray);
  }

  function onSearch(event: ChangeEvent<HTMLInputElement>) {
    const searchLo = event.target.value.toLowerCase();
    setSearchFor(searchLo);
  }

  useEffect(() => {
    if (fullMetaData) {
      setCurrentFullData(getMetaArray());
    }
  }, [fullMetaData, getMetaArray]);

  return (
    <Box sx={{ width: "600px" }}>
      <Stack direction="row" spacing={2} sx={{ padding: "8px" }}>
        <TextField
          id="search"
          type="search"
          value={searchFor}
          onChange={onSearch}
          margin="normal"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
        <Slider
          title="Image"
          marks={Array.from({ length: sliderMax }, (_, i) => {
            const val = i + 1;
            return {
              value: val,
              label: val === 1 || val === sliderMax ? val : "",
            };
          })}
          min={1}
          max={sliderMax}
          onChange={onSliderChange}
          valueLabelDisplay={"auto"}
          valueLabelFormat={(value) => `Image slice ${value}`}
          sx={{
            width: 250,
            margin: "7px",
            marginLeft: 0,
            "& .MuiSlider-markLabel": {
              // Styles for mark labels
              fontSize: "0.75rem",
              lineHeight: "0",
            },
            "& .MuiSlider-thumb": {
              // styles for thumb (dot)
              height: "16px",
              width: "16px",
            },
            "& .MuiSlider-valueLabel": {
              //styles for value hover label
              fontSize: "0.75rem",
              lineHeight: "1rem",
            },
          }}
        />
      </Stack>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Tag</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData?.map((item, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.value}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
